
import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import HomePage from '../pages/home/page';
import AuthPage from '../pages/auth/page';
import AuthConfirmPage from '../pages/auth/confirm/page';
import RecuperarPage from '../pages/auth/recuperar/page';
import DashboardPage from '../pages/dashboard/page';
import NotFound from '../pages/NotFound';
import AuthGuard from '../components/auth/AuthGuard';
import AdminGuard from '../components/auth/AdminGuard';

// =====================================================================
// Las pantallas pesadas se descargan recién cuando se entra a ellas.
//
// Antes la app venía toda en un solo archivo de 930 KB. Eso lo bajaba
// ENTERO cualquiera que abriera la portada, aunque no tuviera cuenta:
// el panel de administración, el chat, las gráficas de crecimiento,
// todo. Con datos móviles en el Perú, eso es plata y es espera.
//
// La gráfica de crecimiento sola pesa unos 300 KB (la librería que la
// dibuja). No tiene sentido cobrársela a alguien que solo quiere anotar
// un pañal.
//
// Portada, acceso y panel se dejan enteros a propósito: son las tres
// primeras pantallas que ve cualquiera, y partirlas haría aparecer un
// "Cargando..." justo en la primera impresión.
//
// El `Suspense` que muestra el cartel mientras llegan ya estaba puesto
// en App.tsx, así que esto no necesita nada más.
// =====================================================================
const FeedingPage     = lazy(() => import('../pages/feeding/page'));
const SleepPage       = lazy(() => import('../pages/sleep/page'));
const DiapersPage     = lazy(() => import('../pages/diapers/page'));
const GrowthPage      = lazy(() => import('../pages/growth/page'));
const VaccinationPage = lazy(() => import('../pages/vaccination/page'));
const AIChatPage      = lazy(() => import('../pages/ai-chat/page'));
const CanjearPage     = lazy(() => import('../pages/canjear/page'));
const AdminPage       = lazy(() => import('../pages/admin/page'));
const CuentaPage      = lazy(() => import('../pages/cuenta/page'));
const InformePage     = lazy(() => import('../pages/informe/page'));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <HomePage />,
  },
  {
    path: '/auth',
    element: <AuthPage />,
  },
  {
    path: '/auth/confirm',
    element: <AuthConfirmPage />,
  },
  {
    // Poner contraseña nueva. Va FUERA del candado de sesión a
    // propósito: quien llega acá justamente no puede entrar.
    path: '/auth/recuperar',
    element: <RecuperarPage />,
  },
  {
    path: '/dashboard',
    element: (
      <AuthGuard>
        <DashboardPage />
      </AuthGuard>
    ),
  },
  {
    path: '/feeding',
    element: (
      <AuthGuard>
        <FeedingPage />
      </AuthGuard>
    ),
  },
  {
    path: '/sleep',
    element: (
      <AuthGuard>
        <SleepPage />
      </AuthGuard>
    ),
  },
  {
    path: '/diapers',
    element: (
      <AuthGuard>
        <DiapersPage />
      </AuthGuard>
    ),
  },
  {
    path: '/growth',
    element: (
      <AuthGuard>
        <GrowthPage />
      </AuthGuard>
    ),
  },
  {
    path: '/vaccination',
    element: (
      <AuthGuard>
        <VaccinationPage />
      </AuthGuard>
    ),
  },
  {
    // Fuera de cualquier candado de suscripción a propósito: quien ya
    // venció TIENE que poder llegar acá para volver a entrar.
    path: '/canjear',
    element: (
      <AuthGuard>
        <CanjearPage />
      </AuthGuard>
    ),
  },
  {
    // Como /canjear: fuera del candado de suscripción. Quien venció
    // tiene que poder llevarse sus datos o borrar su cuenta igual.
    path: '/cuenta',
    element: (
      <AuthGuard>
        <CuentaPage />
      </AuthGuard>
    ),
  },
  {
    path: '/informe',
    element: (
      <AuthGuard>
        <InformePage />
      </AuthGuard>
    ),
  },
  {
    path: '/admin',
    element: (
      <AdminGuard>
        <AdminPage />
      </AdminGuard>
    ),
  },
  {
    path: '/ai-chat',
    element: (
      <AuthGuard>
        <AIChatPage />
      </AuthGuard>
    ),
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;
